// --- START OF FILE About.jsx ---

import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Zap, Server, BarChart3, Briefcase, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import founderPhoto from '../assets/founder-photo.jpg';

const About = () => {
  const coreCompetencies = [
    { icon: Zap, text: 'Low-Latency Networks' },
    { icon: Server, text: 'Data Center Architecture' },
    { icon: BarChart3, text: 'Trading Analytics' },
    { icon: Briefcase, text: 'Project Management' },
  ];

  return (
    <section id="about" className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[hsl(var(--accent-violet))]/5 to-transparent"></div> {/* Use accent-violet for background tint */}
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">Meet the Founder</span> {/* Now uses Oxanium font */}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            An architect with deep expertise in bridging the gap between high-performance technology and financial markets.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <div className="relative w-64 h-64">
              {/* Changed glow to use Primary Gold and Peacock Blue */}
              <div className="absolute -inset-1 bg-gradient-to-br from-[hsl(var(--primary-gold))] to-[hsl(var(--peacock-blue))] rounded-full blur-xl opacity-75"></div>
              <div 
                className="relative w-full h-full rounded-full bg-gray-800 border-2 border-[hsl(var(--border))] bg-cover bg-center"
                style={{ backgroundImage: `url(${founderPhoto})` }} 
              ></div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <h3 className="text-3xl font-bold text-white mb-2">Aditya Krishna Govardhan</h3>
            <p className="text-lg font-semibold gradient-text mb-6">Founder, VegaVelox</p> {/* Now uses Oxanium font */}
            
            <p className="text-gray-300 leading-relaxed mb-4">
              With over a decade of experience at the intersection of finance and technology, Aditya Krishna has led the design and implementation of mission-critical trading infrastructure for top-tier financial institutions. His expertise spans from ultra-low latency network architecture to quantitative trading systems and hardware acceleration.
            </p>
            <p className="text-gray-300 leading-relaxed mb-8">
              At VegaVelox, Aditya's vision is to empower clients with the same level of sophisticated, high-performance technology once reserved for the largest proprietary trading firms, delivering a decisive competitive edge in today’s fast-paced markets.
            </p>
            
            <div className="mb-8">
              <h4 className="text-xl font-semibold text-white mb-6">Core Competencies</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {coreCompetencies.map((skill, index) => (
                  <motion.div
                    key={skill.text}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-3"
                  >
                    <skill.icon className="w-5 h-5 text-[hsl(var(--accent-cyan))] flex-shrink-0" /> {/* Using accent-cyan for a subtle contrasting accent */}
                    <span className="text-gray-300">{skill.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              {/* Updated button to primary-gold theme */}
              <Button
                asChild
                size="lg"
                className="bg-transparent border-2 border-[hsl(var(--primary-gold))] text-[hsl(var(--primary-gold))] hover:bg-[hsl(var(--primary-gold))]/10 px-6 py-3 font-semibold rounded-lg transition-all duration-300"
              >
                <a href="https://www.linkedin.com/in/aditya-krishna-g-product-manager/" target="_blank" rel="noopener noreferrer">
                  <Linkedin className="mr-2 w-5 h-5" />
                  Connect on LinkedIn
                </a>
              </Button>
              
              <Button
                asChild
                size="lg"
                className="bg-transparent border-2 border-[hsl(var(--border))] text-gray-300 hover:bg-gray-500/10 hover:border-gray-400 px-6 py-3 font-semibold rounded-lg transition-all duration-300"
              >
                <a href="https://velsunki3.odoo.com/" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 w-5 h-5" />
                  View Full Portfolio
                </a>
              </Button>
            </div>
            
          </motion.div>
        </div>
      </div>
    </section>

  );
};
export default About;
// --- END OF FILE About.jsx ---